import React, { useState } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel
} from '@mui/material';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

const alphaNumericSorter = (a, b, field, isAscending) => {
  const valueA = a[field] ?? '';
  const valueB = b[field] ?? '';

  if (field === 'dateTime') {
    const dateA = valueA ? moment(valueA).valueOf() : 0;
    const dateB = valueB ? moment(valueB).valueOf() : 0;
    return isAscending ? dateA - dateB : dateB - dateA;
  }

  return isAscending
    ? valueA.toString().localeCompare(valueB.toString(), undefined, { numeric: true })
    : valueB.toString().localeCompare(valueA.toString(), undefined, { numeric: true });
};

const PrintOverviewDataTable = ({ columns, data, tz, sx }) => {
  const [sortField, setSortField] = useState('dateTime');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    setSortOrder((prevOrder) => (sortField === field && prevOrder === 'asc' ? 'desc' : 'asc'));
    setSortField(field);
  };
  const getEverFlagPriority = (row) => {
    const everFlags = row?.everFlags;
    const shouldHide = row?.omitFromSqc === 'N' && row?.silence === 'N';

    if (!Array.isArray(everFlags) || everFlags.length === 0 || shouldHide) return -1;

    const latestFlagEntry = everFlags[everFlags.length - 1];
    const flagString =
      typeof latestFlagEntry === 'string' ? latestFlagEntry : latestFlagEntry?.flags || '';

    if (/warnings:/i.test(flagString)) return 2;
    if (/ooc:/i.test(flagString)) return 1;
    return 0;
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortField === 'everFlags') {
      const prioA = getEverFlagPriority(a);
      const prioB = getEverFlagPriority(b);

      if (prioA !== prioB) return sortOrder === 'asc' ? prioA - prioB : prioB - prioA;

      const aFlag = a?.everFlags?.[a.everFlags.length - 1]?.flags || '';
      const bFlag = b?.everFlags?.[b.everFlags.length - 1]?.flags || '';
      return sortOrder === 'asc' ? aFlag.localeCompare(bFlag) : bFlag.localeCompare(aFlag);
    } else {
      return alphaNumericSorter(a, b, sortField, sortOrder === 'asc');
    }
  });
  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'visible' }}>
        <Table>
          <TableHead sx={sx?.head}>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  sx={{
                    ...sx?.cell,
                    minWidth: col.minWidth || 100
                  }}
                >
                  <TableSortLabel
                    active={sortField === col.field}
                    direction={sortField === col.field ? sortOrder : 'asc'}
                    onClick={() => handleSort(col.field)}
                  >
                    {col.headerName}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow key={row.id || `${row.dateTime}_${index}`} sx={sx?.row}>
                {columns.map((col) => (
                  <TableCell
                    key={`${col.field}_${index}`}
                    sx={{
                      ...(col.field === 'dateTime' ? { whiteSpace: 'nowrap' } : {}),
                      ...sx?.cell,
                      minWidth: col.minWidth || 100,
                      paddingRight: 0,
                      paddingLeft: 2
                    }}
                  >
                    {col.field === 'dateTime' && row[col.field]
                      ? moment.tz(row[col.field], tz || 'UTC').format('MM/DD/YYYY HH:mm')
                      : row[col.field] !== undefined && row[col.field] !== null
                        ? col.field === 'everFlags' && Array.isArray(row[col.field])
                          ? (() => {
                              const value = row[col.field];

                              if (
                                (row?.omitFromSqc === 'N' && row?.silence === 'N') ||
                                !Array.isArray(value) ||
                                value.length === 0
                              ) {
                                return <></>;
                              }

                              const normalize = (str) =>
                                (str || '')
                                  .trim()
                                  .replace(/^W:/i, 'Warnings:')
                                  .replace(/^Warnings:/i, 'Warnings:')
                                  .toLowerCase();

                              const currentFlags = (row.flags || '')
                                .split(/[;,]/)
                                .map(normalize)
                                .filter(Boolean);

                              const latestFlagEntry = value[value.length - 1];
                              const flagString =
                                typeof latestFlagEntry === 'string'
                                  ? latestFlagEntry
                                  : latestFlagEntry?.flags || '';

                              const sections = flagString.split(';');
                              const flagGroups = {};

                              sections.forEach((section) => {
                                const [type, values] = section.split(':');
                                if (!values) return;

                                const prefix = type.trim().replace(/^W$/i, 'Warnings');
                                const individualFlags = values.split(',').map((f) => f.trim());

                                individualFlags.forEach((flag) => {
                                  const fullFlag = `${prefix}: ${flag}`;
                                  const norm = normalize(fullFlag);
                                  if (!currentFlags.includes(norm)) {
                                    if (!flagGroups[prefix]) {
                                      flagGroups[prefix] = new Set();
                                    }
                                    flagGroups[prefix].add(flag);
                                  }
                                });
                              });

                              const groupedFlags = Object.entries(flagGroups).map(
                                ([prefix, flags]) => {
                                  return `${prefix}: ${Array.from(flags).join(', ')}`;
                                }
                              );

                              if (groupedFlags.length === 0) {
                                return <></>;
                              }

                              return (
                                <span>
                                  {groupedFlags.map((flag, i) => (
                                    <p key={i} style={{ margin: 0 }}>
                                      {flag}
                                    </p>
                                  ))}
                                </span>
                              );
                            })()
                          : row[col.field] === 'NaN'
                            ? ''
                            : row[col.field]
                        : '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <br />
    </>
  );
};

PrintOverviewDataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  tz: PropTypes.string,
  sx: PropTypes.object
};

export default PrintOverviewDataTable;
